import type { PortalAudience } from '@/access/portalAccess.types';
import type { JobsViewInput } from '@/modules/jobs/jobs.viewInput';

export type JobFilterKey =
  | 'status'
  | 'client'
  | 'site'
  | 'industry'
  | 'skillSet'
  | 'application'
  | 'daySequenceStatus'
  | 'accountManager';

export type JobsListQueryType = 'tenantByDateRange' | 'clientByDateRange';

const TENANT_FILTER_KEYS: JobFilterKey[] = [
  'status',
  'client',
  'accountManager',
  'site',
  'industry',
  'skillSet',
  'application',
  'daySequenceStatus',
];

const CLIENT_FILTER_KEYS: JobFilterKey[] = [
  'status',
  'site',
  'industry',
  'skillSet',
  'application',
  'daySequenceStatus',
];

export type JobsListView = {
  audience: PortalAudience;
  filterKeys: JobFilterKey[];
  table: {
    showClientColumn: boolean;
    showAccountManagerColumn: boolean;
    showMarginColumn: boolean;
    showCandidateColumn: boolean;
    showApplicationsColumn: boolean;
  };
  actions: {
    showAddJob: boolean;
    showExportReport: boolean;
    showDateRangePicker: boolean;
  };
  query: {
    jobs: {
      strategy: JobsListQueryType;
      includeApplicationCounts: boolean;
    };
    workers: {
      strategy: 'tenant' | 'client' | 'none';
    };
    clients: {
      fetch: boolean;
    };
    classifications: {
      fetch: boolean;
    };
  };
};

export const getJobsListView = ({ access }: JobsViewInput): JobsListView => {
  const {
    audience,
    isTenantPortal,
    isClientPortal,
    canCreateJob,
    canExportTenantReport,
  } = access;

  return {
    audience,
    filterKeys: isTenantPortal ? TENANT_FILTER_KEYS : CLIENT_FILTER_KEYS,
    table: {
      showClientColumn: isTenantPortal,
      showAccountManagerColumn: isTenantPortal,
      showMarginColumn: isTenantPortal && access.canManageTenantJobs,
      showCandidateColumn: true,
      showApplicationsColumn: isTenantPortal || isClientPortal,
    },
    actions: {
      showAddJob: canCreateJob,
      showExportReport: canExportTenantReport,
      showDateRangePicker: true,
    },
    query: {
      jobs: {
        strategy: isTenantPortal ? 'tenantByDateRange' : 'clientByDateRange',
        includeApplicationCounts: isTenantPortal && access.canManageTenantJobs,
      },
      workers: {
        strategy: isTenantPortal ? 'tenant' : 'client',
      },
      clients: {
        fetch: isTenantPortal,
      },
      classifications: {
        fetch: true,
      },
    },
  };
};

/** @deprecated Use getJobsListView */
export const getJobsListViewConfig = getJobsListView;
